import { t, type ErrorCodeMapperEntry } from "./types.js";

/** Error codes 23000-23999. One thousand-block per file: a duplicate number here can only
 *  collide within this file, which is what makes the split safer than one shared object. */
export const externalLinksErrors = {
  // 23xxx is the externalLinks module's series, opened by its SOMETHING_WENT_WRONG (9000) /
  // RESOURCE_NOT_FOUND (9003) split. The module had no codes of its own before this.
  // 23001-23018 — the externalLinks module's generic-code split. Every message is copied verbatim
  // from the call site it replaces.
  EXTERNAL_LINK_RATE_LIMITED: {
    errorCode: 23001,
    errorMessage: t("Too many requests. Please try again shortly."),
    isDynamic: false,
  },
  // 4 call sites, all in externalLinks.download.ts, all answering with HTTP 404.
  EXTERNAL_LINK_FILE_NOT_FOUND: {
    errorCode: 23002,
    errorMessage: t("File not found"),
    isDynamic: false,
  },
  EXTERNAL_LINK_INVALID: {
    errorCode: 23003,
    errorMessage: t("This link is no longer valid"),
    isDynamic: false,
  },
  EXTERNAL_LINK_MODEL_GEOMETRY_NOT_READY: {
    errorCode: 23004,
    errorMessage: t("Model geometry is not available yet"),
    isDynamic: false,
  },
  EXTERNAL_LINK_DOWNLOADS_DISABLED: {
    errorCode: 23005,
    errorMessage: t("Downloads are disabled on this link"),
    isDynamic: false,
  },
  EXTERNAL_LINK_SINGLE_FILE_PREVIEW_ONLY: {
    errorCode: 23006,
    errorMessage: t("Only a single file can be previewed"),
    isDynamic: false,
  },
  // A server-side misconfiguration (no Office preview backend) versus that backend being reachable
  // but failing — two different sentences today, so two codes.
  EXTERNAL_LINK_OFFICE_PREVIEW_NOT_CONFIGURED: {
    errorCode: 23007,
    errorMessage: t("Office preview is not configured"),
    isDynamic: false,
  },
  EXTERNAL_LINK_OFFICE_PREVIEW_UNAVAILABLE: {
    errorCode: 23008,
    errorMessage: t("Office preview is unavailable"),
    isDynamic: false,
  },
  // Replaces the `EXTERNAL_SHARING_DISABLED_MESSAGE` constant (externalLinks.helper.ts).
  EXTERNAL_SHARING_DISABLED: {
    errorCode: 23009,
    errorMessage: t("External sharing is turned off for this portal"),
    isDynamic: false,
  },
  // The item being shared could not be resolved (3 sites). Deliberately distinct from
  // EXTERNAL_LINK_SHARED_ITEM_NOT_FOUND (23015), which words the same idea differently on the
  // already-shared path.
  EXTERNAL_LINK_ITEM_NOT_FOUND: {
    errorCode: 23010,
    errorMessage: t("The selected item no longer exists"),
    isDynamic: false,
  },
  // Full access on the item is required to create or edit its links. Create and edit keep separate
  // codes because each sentence names its own action.
  EXTERNAL_LINK_CREATE_REQUIRES_FULL_ACCESS: {
    errorCode: 23011,
    errorMessage: t("You need full access on this item to create a shared link"),
    isDynamic: false,
  },
  // The link row could not be resolved — 11 call sites, the module's single most repeated failure.
  EXTERNAL_LINK_NOT_FOUND: {
    errorCode: 23012,
    errorMessage: t("The selected link no longer exists"),
    isDynamic: false,
  },
  EXTERNAL_LINK_EDIT_REQUIRES_FULL_ACCESS: {
    errorCode: 23013,
    errorMessage: t("You need full access on this item to edit its shared links"),
    isDynamic: false,
  },
  EXTERNAL_LINK_DISABLED_BY_ADMIN: {
    errorCode: 23014,
    errorMessage: t(
      "This link was disabled in Portal Settings. Ask someone with the Manage Portal External Links permission to re-enable it first.",
    ),
    isDynamic: false,
  },
  EXTERNAL_LINK_SHARED_ITEM_NOT_FOUND: {
    errorCode: 23015,
    errorMessage: t("The shared item no longer exists"),
    isDynamic: false,
  },
  EXTERNAL_LINK_ITEM_NO_ACCESS: {
    errorCode: 23016,
    errorMessage: t("You do not have access to this item"),
    isDynamic: false,
  },
  EXTERNAL_LINKS_REGISTER_ADMIN_ONLY: {
    errorCode: 23017,
    errorMessage: t("You do not have permission to view the external links register."),
    isDynamic: false,
  },
  EXTERNAL_LINKS_DISABLE_ADMIN_ONLY: {
    errorCode: 23018,
    errorMessage: t("You do not have permission to disable or re-enable external links."),
    isDynamic: false,
  },
  // The Office-preview path resolved a single file, but that file type has no converted artifact.
  // Distinct from EXTERNAL_LINK_SINGLE_FILE_PREVIEW_ONLY (23006), which is about the SELECTION.
  EXTERNAL_LINK_NO_CONVERTED_PREVIEW: {
    errorCode: 23019,
    errorMessage: t("This file type has no converted preview"),
    isDynamic: false,
  },
  // Replaces the `DUPLICATE_NAME_MESSAGE` constant (externalLinks.service.ts). Checked only AFTER
  // the access gate, so a name clash is never disclosed to someone without access to the item.
  EXTERNAL_LINK_DUPLICATE_NAME: {
    errorCode: 23020,
    errorMessage: t("A link with this name already exists on this item"),
    isDynamic: false,
  },
  // Full access is required to revoke or re-enable a link. Each verb keeps its own code because each
  // sentence names its own action, matching CREATE (23011) / EDIT (23013).
  EXTERNAL_LINK_REVOKE_REQUIRES_FULL_ACCESS: {
    errorCode: 23021,
    errorMessage: t("You need full access on this item to revoke its shared links"),
    isDynamic: false,
  },
  EXTERNAL_LINK_ENABLE_REQUIRES_FULL_ACCESS: {
    errorCode: 23022,
    errorMessage: t("You need full access on this item to enable its shared links"),
    isDynamic: false,
  },
  EXTERNAL_LINK_EXPIRY_MUST_BE_FUTURE: {
    errorCode: 23023,
    errorMessage: t("The expiry date must be in the future"),
    isDynamic: false,
  },
  // A recipient's 12h share session expired MID-VIEW on a password-protected link: the server answers
  // a SUCCESSFUL envelope carrying `requiresPassword: true` and no permissions, which the web
  // share-viewer path (services/utils/fileViewPermission.ts) turns into a refusal of its own. Distinct
  // from EXTERNAL_LINK_INVALID (23003): the link is still good, it just needs unlocking again.
  EXTERNAL_LINK_UNLOCK_REQUIRED: {
    errorCode: 23024,
    errorMessage: t("This link is password protected. Please unlock it to continue."),
    isDynamic: false,
  },
} satisfies Record<string, ErrorCodeMapperEntry>;
